import React, { useEffect } from 'react';

/**
 * <Seo> — minimal, runtime DOM-mutating SEO helper.
 *
 * We're a Create-React-App SPA so we don't get react-helmet's static
 * head injection at build time; instead this component imperatively
 * writes <title>, the standard meta tags, OpenGraph + Twitter cards and
 * one or more JSON-LD blobs into <head> the moment the page mounts.
 *
 * Each page passes a small props object and gets a Schema.org Service +
 * Organization + (optional) FAQPage payload for free.
 *
 * Why not Helmet:
 *   * one fewer dependency for the SPA bundle
 *   * the admin v2 SEO editor already drives meta for *some* pages via
 *     the backend — this is the fallback used by the bespoke pages
 *     (BusinessVoIP, MicrosoftTeams, ContactCentre etc) that don't go
 *     through ServicePageView's auto-SEO path yet.
 */

const SITE_BASE = 'https://voipinger.com';
const DEFAULT_OG_IMAGE = `${SITE_BASE}/assets/Voip-Phone-Provider.webp`;
const ORGANIZATION = {
  '@type': 'Organization',
  name: 'VoIPinger',
  url: SITE_BASE,
  logo: `${SITE_BASE}/assets/VoIPinger-Main-Logo-1.webp`,
  sameAs: [
    'https://www.youtube.com/@clisecure',
    'https://uk.linkedin.com/company/clisecure'
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+44-20-3355-1151',
    contactType: 'sales',
    areaServed: 'GB',
    availableLanguage: ['en']
  }
};

const KEY = 'data-voipinger-seo';

/** Remove every element we previously injected so navigation between pages
 *  doesn't leak the previous page's meta. */
function purge() {
  document.querySelectorAll(`[${KEY}]`).forEach((el) => el.remove());
}

function meta(attr, value, content) {
  const el = document.createElement('meta');
  el.setAttribute(attr, value);
  el.setAttribute('content', content);
  el.setAttribute(KEY, '1');
  return el;
}

function linkEl(rel, href) {
  const el = document.createElement('link');
  el.setAttribute('rel', rel);
  el.setAttribute('href', href);
  el.setAttribute(KEY, '1');
  return el;
}

function script(json) {
  const el = document.createElement('script');
  el.setAttribute('type', 'application/ld+json');
  el.setAttribute(KEY, '1');
  el.textContent = JSON.stringify(json, null, 2);
  return el;
}

export default function Seo({
  title,
  description,
  path,
  image,
  // structured data
  service,           // { name, description, areaServed?, serviceType? }
  product,           // { name, description, brand?, offers? }
  faq,               // [{ q, a }]
  breadcrumbs,       // [{ name, path }]
}) {
  useEffect(() => {
    const head = document.head;
    purge();

    // <title>
    if (title) document.title = title;

    // Canonical
    const url = path ? `${SITE_BASE}${path.startsWith('/') ? '' : '/'}${path}` : window.location.href;
    head.appendChild(linkEl('canonical', url));

    // Description
    if (description) {
      head.appendChild(meta('name', 'description', description));
    }

    // OpenGraph
    const og = image || DEFAULT_OG_IMAGE;
    head.appendChild(meta('property', 'og:type', 'website'));
    if (title) head.appendChild(meta('property', 'og:title', title));
    if (description) head.appendChild(meta('property', 'og:description', description));
    head.appendChild(meta('property', 'og:url', url));
    head.appendChild(meta('property', 'og:image', og));
    head.appendChild(meta('property', 'og:site_name', 'VoIPinger'));

    // Twitter
    head.appendChild(meta('name', 'twitter:card', 'summary_large_image'));
    if (title) head.appendChild(meta('name', 'twitter:title', title));
    if (description) head.appendChild(meta('name', 'twitter:description', description));
    head.appendChild(meta('name', 'twitter:image', og));

    // ----- JSON-LD blobs -----
    head.appendChild(script({ '@context': 'https://schema.org', ...ORGANIZATION }));

    if (service) {
      head.appendChild(script({
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: service.name || title,
        description: service.description || description,
        provider: { '@type': 'Organization', name: 'VoIPinger', url: SITE_BASE },
        areaServed: service.areaServed || { '@type': 'Country', name: 'United Kingdom' },
        serviceType: service.serviceType || 'Cloud telephony',
        url
      }));
    }

    if (product) {
      head.appendChild(script({
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: product.name || title,
        description: product.description || description,
        brand: { '@type': 'Brand', name: product.brand || 'VoIPinger' },
        image: og,
        url,
        ...(product.offers ? { offers: product.offers } : {})
      }));
    }

    if (faq && faq.length) {
      head.appendChild(script({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faq.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a }
        }))
      }));
    }

    if (breadcrumbs && breadcrumbs.length) {
      head.appendChild(script({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((b, idx) => ({
          '@type': 'ListItem',
          position: idx + 1,
          name: b.name,
          item: `${SITE_BASE}${b.path.startsWith('/') ? '' : '/'}${b.path}`
        }))
      }));
    }

    return () => purge();
  }, [title, description, path, image, service, product, faq, breadcrumbs]);

  return null;
}
